import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type {
  SessionBootstrapResponse,
  SessionGetResponse,
} from "@/lib/api/generated/control-api.schemas";
import { getPostApiSessionsAdminLiveUrl } from "@/lib/api/generated/sessions/sessions";
import { openSessionLiveStream } from "@/lib/api/session-live";
import type {
  SessionLiveEvent,
  SessionLiveRuntimeEvent,
} from "../../../../../packages/shared-types/src/index.js";

export const sessionQueryKeys = {
  bootstrap: (tenantId: string, targetUserId?: string) =>
    targetUserId
      ? (["tenants", tenantId, "sessions", "bootstrap", targetUserId] as const)
      : (["tenants", tenantId, "sessions", "bootstrap"] as const),
  detail: (tenantId: string, sessionId: string, targetUserId?: string) =>
    targetUserId
      ? (["tenants", tenantId, "sessions", sessionId, targetUserId] as const)
      : (["tenants", tenantId, "sessions", sessionId] as const),
};

function sortSessionsByActivity(sessions: SessionBootstrapResponse["sessions"]) {
  return sessions.toSorted((left, right) => {
    const leftTs = Date.parse(left.lastMessageAt || left.updatedAt || left.createdAt || "");
    const rightTs = Date.parse(right.lastMessageAt || right.updatedAt || right.createdAt || "");
    return rightTs - leftTs;
  });
}

export function upsertSessionSummary(
  current: SessionBootstrapResponse | undefined,
  session: SessionBootstrapResponse["sessions"][number],
): SessionBootstrapResponse | undefined {
  if (!current) {
    return current;
  }

  const existing = current.sessions.filter((entry) => entry.sessionId !== session.sessionId);
  return {
    ...current,
    sessions: sortSessionsByActivity([session, ...existing]),
  };
}

export function applySessionLiveEventToCache(
  queryClient: QueryClient,
  tenantId: string,
  event: SessionLiveEvent,
  targetUserId?: string,
): void {
  if (event.type === "sessions.changed") {
    queryClient.setQueryData<SessionBootstrapResponse | undefined>(
      sessionQueryKeys.bootstrap(tenantId, targetUserId),
      (current) => upsertSessionSummary(current, event.session),
    );
    queryClient.setQueryData<SessionGetResponse | undefined>(
      sessionQueryKeys.detail(tenantId, event.session.sessionId, targetUserId),
      (current) => (current ? { ...current, session: event.session } : current),
    );
    return;
  }

  if (event.type !== "session.message") {
    return;
  }

  queryClient.setQueryData<SessionGetResponse | undefined>(
    sessionQueryKeys.detail(tenantId, event.sessionId, targetUserId),
    (current) => {
      if (!current) {
        return current;
      }

      const messages = current.messages.filter(
        (message) => message.messageId !== event.message.messageId,
      );
      return {
        ...current,
        session: {
          ...current.session,
          lastMessageText: event.message.text,
          lastMessageAt: event.message.createdAt,
          updatedAt: event.message.createdAt,
          messageCount: Math.max(current.session.messageCount, messages.length + 1),
        },
        messages: [...messages, event.message].toSorted((left, right) =>
          `${left.createdAt}:${left.messageId}`.localeCompare(
            `${right.createdAt}:${right.messageId}`,
          ),
        ),
      };
    },
  );
}

export function useSessionLiveUpdates(params: {
  tenantId?: string;
  sessionIds: string[];
  targetUserId?: string;
  onRuntimeEvent: (event: SessionLiveRuntimeEvent) => void;
  onAssistantMessage: (sessionId: string) => void;
}) {
  const queryClient = useQueryClient();
  const sessionIdsKey = params.sessionIds.join("|");

  useEffect(() => {
    if (!params.tenantId || params.sessionIds.length === 0) {
      return;
    }

    const controller = new AbortController();
    void (async () => {
      while (!controller.signal.aborted) {
        try {
          await openSessionLiveStream({
            request: {
              ...(params.targetUserId ? { targetUserId: params.targetUserId } : {}),
              surface: "web",
              sessionIds: params.sessionIds,
            },
            ...(params.targetUserId ? { path: getPostApiSessionsAdminLiveUrl() } : {}),
            signal: controller.signal,
            onEvent: (event) => {
              applySessionLiveEventToCache(
                queryClient,
                params.tenantId!,
                event,
                params.targetUserId,
              );
              if (event.type === "session.runtime") {
                params.onRuntimeEvent(event);
              }
              if (event.type === "session.message" && event.message.role === "assistant") {
                params.onAssistantMessage(event.sessionId);
              }
            },
          });
        } catch (error) {
          if (controller.signal.aborted) {
            return;
          }
          console.error("[sessions-live] stream failed", error);
        }

        if (!controller.signal.aborted) {
          await new Promise((resolve) => window.setTimeout(resolve, 1_000));
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [
    sessionIdsKey,
    queryClient,
    params.onAssistantMessage,
    params.onRuntimeEvent,
    params.targetUserId,
    params.tenantId,
  ]);
}
